import { DocumentDto } from "@core/data/dto/document.dto";
import { IdentificationDocumentTypeEnumDto } from "@core/data/dto/identification-document-type.dto";
import { PaginatedDto } from "@core/data/dto/paginated.dto";
import { OrderField } from "@core/domain/models/order.model";
import type { Nullable } from "@core/domain/types/nullable.type";
import { Nullish } from "@core/domain/types/nullish.type";
import { Type } from "class-transformer";
import {
    IsArray,
    IsDateString,
    IsEnum,
    IsNotEmpty,
    IsNumber,
    IsOptional,
    IsString,
} from "class-validator";
import {
    DepositAnotherSourcesFinancingTypeEnum,
    DepositOwnFinancingTypeEnum,
    DepositSubventionTypeEnum,
    DepositTypeEnum,
} from "../../domain/models/deposit-type.model";
import { Deposit } from "../../domain/models/deposit.model";
import { IdentificationTypeEnumDto } from "./identification-type.dto";
import { PaymentMethodTypeEnumDto } from "./payment-method-type.dto";
import { IvaExemptionTypeEnumDto } from "./iva-exemption-type.dto";
import { IrpfRetentionTypeEnumDto } from "./irpf-retention-type.dto";
import { IrpfRetentionPercentageTypeEnumDto } from "./irpf-retention-percentage-type.dto";
import { ReasonTypeEnumDto } from "./reason-type-enum.dto";

export class CreateDepositDto {
    @IsNumber()
    @IsNotEmpty()
    id!: number;

    @IsString()
    @IsNotEmpty()
    concept!: string;

    @IsString()
    @IsOptional()
    others_type!: Nullable<string>;

    @IsNumber()
    @IsOptional()
    income_linked_another_project!: Nullable<number>;

    @IsString()
    @IsOptional()
    observations!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    amount!: number;

    @IsDateString()
    @IsNotEmpty()
    deposit_date!: string;

    @IsDateString()
    @IsOptional()
    communication_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    invoice_issue_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    resolution_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    execution_date!: Nullable<string>;

    @IsEnum(PaymentMethodTypeEnumDto)
    @IsOptional()
    payment_method!: Nullable<PaymentMethodTypeEnumDto>;

    @IsEnum(IvaExemptionTypeEnumDto)
    @IsOptional()
    iva_exemption_type!: Nullable<IvaExemptionTypeEnumDto>;

    @IsEnum(IrpfRetentionTypeEnumDto)
    @IsOptional()
    irpf_retention_type!: Nullable<IrpfRetentionTypeEnumDto>;

    @IsEnum(IrpfRetentionPercentageTypeEnumDto)
    @IsOptional()
    irpf_retention_percentage_type!: Nullable<IrpfRetentionPercentageTypeEnumDto>;

    @IsEnum(ReasonTypeEnumDto)
    @IsOptional()
    reason_type!: Nullable<ReasonTypeEnumDto>;

    @IsEnum(IdentificationDocumentTypeEnumDto)
    @IsOptional()
    identification_type!: Nullable<IdentificationTypeEnumDto>;

    @IsString()
    @IsOptional()
    identification!: Nullable<string>;

    @IsString()
    @IsOptional()
    street!: Nullable<string>;

    @IsEnum(IdentificationTypeEnumDto)
    @IsOptional()
    recipient_identification_type!: Nullable<IdentificationTypeEnumDto>;

    @IsString()
    @IsOptional()
    recipient_identification!: Nullable<string>;

    @IsString()
    @IsOptional()
    recipient_street!: Nullable<string>;

    @IsString()
    @IsOptional()
    financing_organization!: Nullable<string>;

    @IsString()
    @IsOptional()
    issuer!: Nullable<string>;

    @IsString()
    @IsOptional()
    recipient!: Nullable<string>;

    @IsString()
    @IsOptional()
    other_payment_source!: Nullable<string>;

    @IsNumber()
    @IsOptional()
    expedient_number!: Nullable<number>;

    @IsString()
    @IsOptional()
    deposit_year!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    tax!: number;

    @IsNumber()
    @IsNotEmpty()
    base_amount!: number;

    @IsString()
    @IsOptional()
    name!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    entity!: number;

    @IsNumber()
    @IsOptional()
    document!: Nullable<number>;

    @IsString()
    @IsOptional()
    scope_detail!: Nullable<string>; // Add scope_detail for backend compatibility

    @IsString()
    @IsOptional()
    type!: Nullable<string>;

    @IsString()
    @IsOptional()
    subtype!: Nullable<string>;

    @IsString()
    @IsOptional()
    category!: Nullable<string>;

    @IsString()
    @IsOptional()
    scope!: Nullable<string>;

    // --- Hierarchical deposit type fields ---
    @IsString()
    @IsOptional()
    deposit_type_id!: Nullable<string>; // Main type id from hierarchy

    @IsString()
    @IsOptional()
    deposit_subtype_id!: Nullable<string>; // Subtype id from hierarchy

    @IsString()
    @IsOptional()
    deposit_category_id!: Nullable<string>; // Category id from hierarchy

    @IsString()
    @IsOptional()
    deposit_scope_id!: Nullable<string>; // Scope id from hierarchy
}

export class SplitDepositDto {
    @IsNumber()
    @IsNotEmpty()
    id!: number;

    @IsNumber()
    @IsNotEmpty()
    project!: number;

    @IsNumber()
    @IsNotEmpty()
    deposit!: number;

    @IsNumber()
    @IsNotEmpty()
    assigned_percentage!: number;
}

export class CreateSplitDepositDto {
    @IsNumber()
    @IsNotEmpty()
    project!: number;

    @IsNumber()
    @IsNotEmpty()
    deposit!: number;

    @IsNumber()
    @IsNotEmpty()
    assigned_percentage!: number;
}

export class DepositDto {
    @IsNumber()
    @IsNotEmpty()
    id!: number;

    @IsString()
    @IsNotEmpty()
    concept!: string;

    @IsString()
    @IsOptional()
    others_type!: Nullable<string>;

    @IsNumber()
    @IsOptional()
    income_linked_another_project!: Nullable<number>;

    @IsString()
    @IsOptional()
    observations!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    amount!: number;

    @IsDateString()
    @IsNotEmpty()
    deposit_date!: string;

    @IsDateString()
    @IsOptional()
    communication_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    invoice_issue_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    resolution_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    execution_date!: Nullable<string>;

    @IsEnum(PaymentMethodTypeEnumDto)
    @IsOptional()
    payment_method!: Nullable<PaymentMethodTypeEnumDto>;

    @IsEnum(IvaExemptionTypeEnumDto)
    @IsOptional()
    iva_exemption_type!: Nullable<IvaExemptionTypeEnumDto>;

    @IsEnum(IrpfRetentionTypeEnumDto)
    @IsOptional()
    irpf_retention_type!: Nullable<IrpfRetentionTypeEnumDto>;

    @IsEnum(IrpfRetentionPercentageTypeEnumDto)
    @IsOptional()
    irpf_retention_percentage_type!: Nullable<IrpfRetentionPercentageTypeEnumDto>;

    @IsEnum(ReasonTypeEnumDto)
    @IsOptional()
    reason_type!: Nullable<ReasonTypeEnumDto>;

    @IsEnum(IdentificationTypeEnumDto)
    @IsOptional()
    identification_type!: Nullable<IdentificationTypeEnumDto>;

    @IsString()
    @IsOptional()
    identification!: Nullable<string>;

    @IsString()
    @IsOptional()
    street!: Nullable<string>;

    @IsEnum(IdentificationTypeEnumDto)
    @IsOptional()
    recipient_identification_type!: Nullable<IdentificationTypeEnumDto>;

    @IsString()
    @IsOptional()
    recipient_identification!: Nullable<string>;

    @IsString()
    @IsOptional()
    recipient_street!: Nullable<string>;

    @IsString()
    @IsOptional()
    financing_organization!: Nullable<string>;

    @IsString()
    @IsOptional()
    issuer!: Nullable<string>;

    @IsString()
    @IsOptional()
    recipient!: Nullable<string>;

    @IsString()
    @IsOptional()
    other_payment_source!: Nullable<string>;

    @IsNumber()
    @IsOptional()
    expedient_number!: Nullable<number>;

    @IsString()
    @IsOptional()
    deposit_year!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    tax!: number;

    @IsNumber()
    @IsNotEmpty()
    base_amount!: number;

    @IsString()
    @IsOptional()
    name!: Nullable<string>;

    @IsNumber()
    @IsNotEmpty()
    entity!: number;

    @IsNumber()
    @IsOptional()
    project!: Nullable<number>;

    @IsNumber()
    @IsOptional()
    document!: Nullable<number>;

    @IsString()
    @IsOptional()
    scope_detail!: Nullable<string>; // Add scope_detail for backend compatibility

    @IsString()
    @IsOptional()
    type!: Nullable<string>;

    @IsString()
    @IsOptional()
    subtype!: Nullable<string>;

    @IsString()
    @IsOptional()
    category!: Nullable<string>;

    @IsString()
    @IsOptional()
    scope!: Nullable<string>;

    @IsOptional()
    @Type(() => DocumentDto)
    document_data!: Nullable<DocumentDto>;

    @IsOptional()
    @Type(() => SplitDepositDto)
    split_deposit_data!: Nullable<SplitDepositDto[]>;

    // --- Hierarchical deposit type fields ---
    @IsString()
    @IsOptional()
    deposit_type_id!: Nullable<string>; // Main type id from hierarchy

    @IsString()
    @IsOptional()
    deposit_subtype_id!: Nullable<string>; // Subtype id from hierarchy

    @IsString()
    @IsOptional()
    deposit_category_id!: Nullable<string>; // Category id from hierarchy

    @IsString()
    @IsOptional()
    deposit_scope_id!: Nullable<string>; // Scope id from hierarchy
}

export class DepositResultDto extends DepositDto {
    @IsArray()
    @Type(() => DepositDto)
    results!: DepositDto[];
}

export class DepositsDto extends PaginatedDto {
    @IsArray()
    @Type(() => DepositDto)
    results!: DepositDto[];
}

// Removed DepositQuery export, as it is not used or needed

// Remove depositOrderMap or update its type to match only the fields that exist on DepositDto
